import { Download, FolderOpenOutlined, OpenInNew, Upload, Visibility } from '@mui/icons-material';
import {
    Button,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ChangeEvent, ReactNode, useRef, useState } from 'react';

import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';
import DocumentDescriptionModal, {
    DocumentDescriptionInfoButton,
    DocumentDescriptionSource,
} from 'views/v2/Dashboards-B2B/components/backOfficeFlow/documents/components/documentDescriptionModal';

import { ACTIONS } from 'constant';
import useTranslate from 'hooks/useTranslate';
import { DocumentInterface } from 'types';

interface Props {
    loading?: boolean;
    uploadingDocumentId?: number | null;
    documents: DocumentInterface[];
    handleAction: (action: ACTIONS, documentTemplate: DocumentInterface) => void;
    handleUploadFile: (documentTemplate: DocumentInterface, file: File) => Promise<void>;
    getTemplateUrl?: (documentTemplate: DocumentInterface) => string | null;
}

const actionButtonSx = { textTransform: 'none', flexShrink: 0 };

const MobileActionRow = ({ label, action }: { label: string; action: ReactNode }) => (
    <Stack
        direction="row"
        spacing={1}
        sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.75,
            borderTop: 1,
            borderColor: 'divider',
        }}
    >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
            {label}
        </Typography>
        {action}
    </Stack>
);

const EmptyAction = () => (
    <Typography variant="body2" color="text.disabled">
        -
    </Typography>
);

const documentHeaders = ['name', 'documentsSample', 'template', 'actions'];

const headCellSx = {
    py: 1.25,
    px: 1.5,
    fontWeight: 700,
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap' as const,
};

const EmptyDocuments = () => {
    const { translate } = useTranslate();

    return (
        <Result
            icon={<FolderOpenOutlined sx={{ fontSize: 32 }} />}
            title={translate('noOrganizationDocuments')}
            description={translate('noOrganizationDocumentsDescription')}
        />
    );
};

const DocumentTable = ({
    documents,
    handleAction,
    handleUploadFile,
    getTemplateUrl,
    loading = false,
    uploadingDocumentId = null,
}: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [selectedDocument, setSelectedDocument] = useState<DocumentInterface | null>(null);
    const [descriptionDocument, setDescriptionDocument] =
        useState<DocumentDescriptionSource | null>(null);

    const openDescription = (document: DocumentInterface) => {
        setDescriptionDocument({
            document_name: document.document_name,
            explanation: document.explanation,
            description_en: document.description_en,
            description_fr: document.description_fr,
        });
    };

    const descriptionModal = descriptionDocument ? (
        <DocumentDescriptionModal
            document={descriptionDocument}
            onClose={() => setDescriptionDocument(null)}
        />
    ) : null;

    const openUploadPicker = (document: DocumentInterface) => {
        setSelectedDocument(document);
        fileRef.current?.click();
    };

    const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file || !selectedDocument) {
            event.target.value = '';
            return;
        }

        try {
            await handleUploadFile(selectedDocument, file);
        } finally {
            event.target.value = '';
            setSelectedDocument(null);
        }
    };

    if (loading) {
        return <TableSkeleton />;
    }

    if (isMobile) {
        if (!documents.length) {
            return <EmptyDocuments />;
        }

        return (
            <Stack spacing={1.5}>
                <input
                    ref={fileRef}
                    type="file"
                    hidden
                    onChange={onFileChange}
                    aria-label={translate('uploadDocument')}
                />
                {documents.map((document) => {
                    const templateUrl = document.template_uri
                        ? null
                        : (getTemplateUrl?.(document) ?? null);
                    const isExternal = Boolean(templateUrl);
                    const hasTemplate = Boolean(document.template_uri || templateUrl);

                    return (
                        <Paper
                            key={document.id}
                            variant="outlined"
                            sx={{ px: 2, pt: 1.5, pb: 0.5 }}
                        >
                            <Stack spacing={1} sx={{ pb: 0.5 }}>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{ alignItems: 'center', minWidth: 0 }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            minWidth: 0,
                                            flex: 1,
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {document.document_name}
                                    </Typography>
                                    <DocumentDescriptionInfoButton
                                        document={document}
                                        onClick={() => openDescription(document)}
                                    />
                                </Stack>
                            </Stack>

                            <MobileActionRow
                                label={translate('documentsSample')}
                                action={
                                    document.sample_uri ? (
                                        <Button
                                            size="small"
                                            startIcon={<Visibility fontSize="small" />}
                                            onClick={() =>
                                                handleAction(ACTIONS.PREVIEW_SAMPLE, document)
                                            }
                                            sx={actionButtonSx}
                                        >
                                            {translate('preview')}
                                        </Button>
                                    ) : (
                                        <EmptyAction />
                                    )
                                }
                            />
                            <MobileActionRow
                                label={translate('template')}
                                action={
                                    hasTemplate ? (
                                        <Button
                                            size="small"
                                            startIcon={
                                                isExternal ? (
                                                    <OpenInNew fontSize="small" />
                                                ) : (
                                                    <Visibility fontSize="small" />
                                                )
                                            }
                                            onClick={() =>
                                                handleAction(ACTIONS.PREVIEW_FILE, document)
                                            }
                                            sx={actionButtonSx}
                                        >
                                            {isExternal
                                                ? translate('openLink')
                                                : translate('preview')}
                                        </Button>
                                    ) : (
                                        <EmptyAction />
                                    )
                                }
                            />
                            <MobileActionRow
                                label={translate('viewDocument')}
                                action={
                                    document.resource_name ? (
                                        <Button
                                            size="small"
                                            startIcon={<Visibility fontSize="small" />}
                                            onClick={() => handleAction(ACTIONS.SELECT, document)}
                                            sx={actionButtonSx}
                                        >
                                            {translate('view')}
                                        </Button>
                                    ) : (
                                        <EmptyAction />
                                    )
                                }
                            />
                            <MobileActionRow
                                label={translate('upload')}
                                action={
                                    <Button
                                        size="small"
                                        startIcon={<Upload fontSize="small" />}
                                        disabled={uploadingDocumentId === document.id}
                                        onClick={() => openUploadPicker(document)}
                                        sx={actionButtonSx}
                                    >
                                        {translate('upload')}
                                    </Button>
                                }
                            />
                            <MobileActionRow
                                label={translate('download')}
                                action={
                                    <Button
                                        size="small"
                                        startIcon={<Download fontSize="small" />}
                                        disabled={!document.resource_name}
                                        onClick={() => handleAction(ACTIONS.DOWNLOAD, document)}
                                        sx={actionButtonSx}
                                    >
                                        {translate('download')}
                                    </Button>
                                }
                            />
                        </Paper>
                    );
                })}
                {descriptionModal}
            </Stack>
        );
    }

    if (!documents.length) {
        return <EmptyDocuments />;
    }

    return (
        <>
            <input
                ref={fileRef}
                type="file"
                hidden
                onChange={onFileChange}
                aria-label={translate('uploadDocument')}
            />
            <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 520, overflow: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 920 }}>
                        <TableHead>
                            <TableRow>
                                {documentHeaders.map((value) => (
                                    <TableCell key={value} sx={headCellSx}>
                                        {translate(value)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documents.map((document) => (
                                <TableRow
                                    key={document.id}
                                    hover
                                    sx={{
                                        '&:nth-of-type(even)': {
                                            backgroundColor: 'rgba(148, 163, 184, 0.05)',
                                        },
                                        '& td': {
                                            borderBottomColor: 'rgba(148, 163, 184, 0.16)',
                                        },
                                    }}
                                >
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{ alignItems: 'center' }}
                                        >
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {document.document_name}
                                            </Typography>
                                            <DocumentDescriptionInfoButton
                                                document={document}
                                                onClick={() => openDescription(document)}
                                            />
                                        </Stack>
                                    </TableCell>

                                    <TableCell>
                                        {document.sample_uri ? (
                                            <Button
                                                disableRipple
                                                size="small"
                                                startIcon={<Visibility fontSize="small" />}
                                                onClick={() =>
                                                    handleAction(ACTIONS.PREVIEW_SAMPLE, document)
                                                }
                                            >
                                                {translate('preview')}
                                            </Button>
                                        ) : (
                                            '---'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const templateUrl = document.template_uri
                                                ? null
                                                : (getTemplateUrl?.(document) ?? null);
                                            const isExternal = Boolean(templateUrl);

                                            if (!document.template_uri && !templateUrl) {
                                                return '---';
                                            }

                                            return (
                                                <Button
                                                    disableRipple
                                                    size="small"
                                                    startIcon={
                                                        isExternal ? (
                                                            <OpenInNew fontSize="small" />
                                                        ) : (
                                                            <Visibility fontSize="small" />
                                                        )
                                                    }
                                                    onClick={() =>
                                                        handleAction(ACTIONS.PREVIEW_FILE, document)
                                                    }
                                                >
                                                    {isExternal
                                                        ? translate('openLink')
                                                        : translate('preview')}
                                                </Button>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{ justifyContent: 'flex-end' }}
                                        >
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                disabled={!document.resource_name}
                                                onClick={() =>
                                                    handleAction(ACTIONS.SELECT, document)
                                                }
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                disabled={uploadingDocumentId === document.id}
                                                onClick={() => openUploadPicker(document)}
                                            >
                                                <Upload fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                aria-label={translate('download')}
                                                disabled={!document.resource_name}
                                                onClick={() =>
                                                    handleAction(ACTIONS.DOWNLOAD, document)
                                                }
                                            >
                                                <Download fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            {descriptionModal}
        </>
    );
};

export default DocumentTable;
